// pages/AttemptPlayer/components/widgets/VoiceWidget.tsx
import React, { useEffect, useRef, useState } from "react";
import type { ResponseVoice } from "../../../ManageQuestions/types";
import WebService from "../../../../Services/WebService";
import {
  postJson,
  startRealtimeTranscription,
  type RealtimeTranscription,
} from "./realtimeTranscriber";

// How long to wait after "stop" for the live transcript before falling back to
// the Whisper upload (normally it arrives in ~0.6 s).
const REALTIME_WAIT_MS = 4000;

type Props = {
  response: ResponseVoice;
  disabled?: boolean;
  value?: { transcript?: string; url?: string } | null;
  onCommit: (payload: {
    transcript?: string;
    url?: string;
    matched?: boolean;
    matchedSets?: number;
    blob?: Blob;

    // NEW: server-driven evaluation coming from /core/transcript
    server_text?: string;
    server_eval?: any;
  }) => void;
  reveal?: boolean;
  controlledRecording?: boolean; // true => start, false => stop
  attemptUuid?: string;
  responseId?: string;

  // lets the player start timer exactly when mic is ready, and show "Analyzing"
  onMicState?: (state: "ready" | "stopped" | "analyzing") => void;
};

type SRWin = Window & {
  webkitSpeechRecognition?: any;
  SpeechRecognition?: any;
};

const getSR = () => {
  const w = window as unknown as SRWin;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

// tiny beeps
const tone = (freq = 660, ms = 120) => {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = freq;
    g.gain.value = 0.12;
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    setTimeout(() => {
      try {
        o.stop();
        o.disconnect();
        g.disconnect();
        ctx.close?.();
      } catch { }
    }, ms);
  } catch { }
};

const normalize = (s: string) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^0-9a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const NUM_MAP: Record<string, string> = {
  zero: "0",
  one: "1",
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
  ten: "10",
  eleven: "11",
  twelve: "12",
};
/** "Runway One Zero" → ["Runway","1","0"] */
const phraseToExpectedTokens = (phrase: string): string[] =>
  (phrase || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((tok) => {
      const n = normalize(tok);
      if (NUM_MAP[n]) return NUM_MAP[n];
      if (/^\d+$/.test(n)) return n;
      return tok;
    });

const VoiceWidget: React.FC<Props> = ({
  response,
  disabled,
  value,
  onCommit,
  reveal,
  controlledRecording,
  attemptUuid,
  responseId,
  onMicState,
}) => {
  const mediaRef = useRef<MediaRecorder | null>(null);
  const [recording, setRecording] = useState(false);
  const srRef = useRef<any>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  // live (streaming) transcription for instant feedback — optional fast path
  const rtRef = useRef<RealtimeTranscription | null>(null);

  useEffect(() => () => rtRef.current?.abort(), []);

  const partialRef = useRef<string>("");
  const finalRef = useRef<string>(value?.transcript || "");

  useEffect(() => {
    finalRef.current = value?.transcript || "";
  }, [value?.transcript]);

  const startSR = () => {
    const SR = getSR();
    if (!SR) return;
    const rec = new SR();
    srRef.current = rec;
    rec.lang = response.voice?.language || "en-IN";
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (evt: any) => {
      let interim = "";
      let fin = "";
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        const r = evt.results[i];
        if (r.isFinal) fin += r[0].transcript;
        else interim += r[0].transcript;
      }
      partialRef.current = interim;
      if (fin) {
        finalRef.current = (finalRef.current ? finalRef.current + " " : "") + fin.trim();
      }
    };
    try {
      rec.start();
    } catch { }
  };

  const stopSR = () => {
    try {
      srRef.current?.stop?.();
    } catch { }
    srRef.current = null;
  };

  const start = async () => {
    if (disabled || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  //     const rec = new MediaRecorder(stream, {
  // mimeType: "audio/webm;codecs=opus",
  //    });
const rec = new MediaRecorder(stream, {
  mimeType: "audio/webm",
});
      chunksRef.current = [];

      // flip UI only when mic is actually hot
      rec.onstart = () => {
        tone(820, 120);
        setRecording(true);
        onMicState?.("ready");
        startSR();
        // Stream the same mic to the live transcriber while the student speaks.
        rtRef.current?.abort();
        rtRef.current = startRealtimeTranscription(
          stream,
          (response.voice?.language || "en").split("-")[0],
        );
      };

      rec.ondataavailable = (e) => {
        if (e?.data) chunksRef.current.push(e.data);
      };

      rec.onstop = async () => {
        stopSR();

//        const blob = new Blob(chunksRef.current, {
//   type: "audio/webm;codecs=opus",
// });
const blob = new Blob(chunksRef.current, {
  type: "audio/webm",
});

        chunksRef.current = [];

        // analyzing…
        onMicState?.("analyzing");

        // (1) upload and (2) scoring run IN PARALLEL — scoring doesn't need the
        // uploaded URL, and running them one after the other made "Analyzing
        // data…" wait for the upload before Whisper even started.

        /* ---------- (1) Upload audio so the attempt has URL ---------- */
        const uploadTask = (async (): Promise<string | undefined> => {
          try {
            const fdUp = new FormData();
            fdUp.append("file", blob, "recording.webm");
            if (attemptUuid) fdUp.append("attempt_uuid", attemptUuid);
            if (responseId) fdUp.append("response_id", responseId);
            fdUp.append("lang", response.voice?.language || "en-IN");

            const up: any = await WebService.fileUploadAPI({
              action: `/training/attempts/${attemptUuid || ""}/upload-voice`,
              body: fdUp,
            });
            return up?.url || up?.path;
          } catch (err) {
            // ignore upload error; we can still score
            console.warn("Voice upload failed:", err);
            return undefined;
          }
        })();

        // Build expected[] from ALL allowed phrases
        const ek = (response as any)?.evaluation?.expected_keywords || [];
        const phrases: string[] = ek.flatMap((set: any) => set?.any_of || []);
        const expectedTokens: string[] = phrases.flatMap(phraseToExpectedTokens);
        const scoreLang = (response.voice?.language || "en").split("-")[0];

        /* ---------- (2) Score on backend via /core/transcript (Whisper) ---------- */
        // Unchanged scoring path; now the FALLBACK when the live transcript isn't available.
        const whisperScore = async (): Promise<{ eval: any; text?: string }> => {
          try {
            const fd = new FormData();
            fd.append("audio", blob, "clip.webm");
            fd.append("language", scoreLang);
            expectedTokens.forEach((w) => fd.append("expected[]", w)); // repeated fields

            const scored: any = await WebService.fileUploadAPI({
              action: `/core/transcript`,
              body: fd,
            });

            const res = scored?.result || scored;
            return {
              eval: res?.evaluation || null,
              text: res?.text || res?.raw?.text || undefined,
            };
          } catch (err) {
            console.warn("Scoring (/core/transcript) failed:", err);
            return { eval: null };
          }
        };

        /* ---------- (2a) Instant path: live transcript -> score text only ---------- */
        // The transcript was streamed while the student spoke, so it's ready ~0.6 s
        // after stop; scoring the text is the same KeywordScorer as /core/transcript.
        const rt = rtRef.current;
        rtRef.current = null;
        const scoreTask = (async (): Promise<{ eval: any; text?: string }> => {
          if (rt) {
            try {
              const liveText = await rt.finish(REALTIME_WAIT_MS);
              if (liveText) {
                const scored: any = await postJson("/core/score-text", {
                  text: liveText,
                  language: scoreLang,
                  expected: expectedTokens,
                });
                const res = scored?.result || scored;
                if (res?.evaluation || !expectedTokens.length) {
                  return {
                    eval: res?.evaluation || null,
                    text: res?.text || res?.raw?.text || undefined,
                  };
                }
              }
            } catch (err) {
              console.warn("Live transcription failed, using Whisper:", err);
            }
          }
          return whisperScore();
        })();

        const [audioUrl, scoredResult] = await Promise.all([uploadTask, scoreTask]);
        const server_eval: any = scoredResult.eval;
        const server_text: string | undefined = scoredResult.text;

        /* ---------- (3) Derive matched flags (kept for backward compat) ---------- */
        let matchedSets = 0;
        let matched = false;
        try {
          const best = Array.isArray(server_eval?.best_result) ? server_eval.best_result : [];
          matchedSets = best.filter((b: any) => String(b?.flag || "").toUpperCase() === "CORRECT").length;
          const exp0 = phrasesFromResponse(response)[0] || "";
          const expTokens = exp0 ? exp0.split(/\s+/).filter(Boolean).length : 0;
          if (expTokens > 0) matched = matchedSets >= expTokens;
        } catch { }

        /* ---------- (4) Commit back to AttemptPlayer ---------- */
        onCommit({
          url: audioUrl,
          transcript: (finalRef.current + " " + (partialRef.current || "")).trim() || undefined,
          matched,
          matchedSets,
          blob,
          server_text,
          server_eval,
        });

        // stop tone + notify
        tone(420, 120);
        onMicState?.("stopped");

        try {
          rec.stream.getTracks().forEach((t) => t.stop());
        } catch { }
        mediaRef.current = null;
        setRecording(false);
      };

      mediaRef.current = rec;
      // important: start -> onstart will set the ready state & tone
      rec.start();
      // (SR is started in onstart)
    } catch (e) {
      rtRef.current?.abort();
      rtRef.current = null;
      console.warn("getUserMedia failed:", e);
      // best-effort commit so UI doesn't hang
      onCommit({ transcript: finalRef.current || undefined });
    }
  };

  const stop = () => {
    if (!recording) return;
    try {
      if (mediaRef.current && mediaRef.current.state !== "inactive") {
        mediaRef.current.stop();
      }
    } catch { }
  };

  // obey parent mic toggle
  useEffect(() => {
    if (controlledRecording == null) return;
    if (controlledRecording) start();
    else stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledRecording]);

  return <div className="tap-voice" />;
};

/* helper: first phrase from evaluation for quick counts */
function phrasesFromResponse(resp: ResponseVoice): string[] {
  const ek = (resp as any)?.evaluation?.expected_keywords || [];
  return ek.flatMap((set: any) => set?.any_of || []);
}

export default VoiceWidget;
